import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from './ui/Modal';
import { useToast } from './ui/Toast';
import { api, ApiError, AccountingPeriod } from '../lib/api';
import { periodSchema, type PeriodFormData } from '../schemas/period';

interface PeriodFormModalProps {
  period?: AccountingPeriod | null;
  entityId: string;
  onClose: () => void;
  onSuccess: (createdPeriodId?: string) => void;
}

export default function PeriodFormModal({
  period,
  entityId,
  onClose,
  onSuccess,
}: PeriodFormModalProps) {
  const toast = useToast();

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PeriodFormData>({
    resolver: zodResolver(periodSchema),
    defaultValues: {
      name: period?.name || '',
      startDate: formatDateForInput(period?.startDate),
      endDate: formatDateForInput(period?.endDate),
      status: period?.status || 'open',
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: PeriodFormData) => {
      const { name, startDate, endDate } = data;
      return api.admin.periods.create(entityId, { name, startDate, endDate });
    },
    onSuccess: (response) => {
      toast.success('Period created successfully');
      onSuccess(response?.data?.id);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: PeriodFormData) => {
      const res = await fetch(`/api/admin/periods/${period!.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Entity-Id': entityId,
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = body?.error?.message || body?.message || 'Update failed';
        throw new Error(msg);
      }
      return body;
    },
    onSuccess: () => {
      toast.success('Period updated successfully');
      onSuccess();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onSubmit = (data: PeriodFormData) => {
    if (period) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={true} onClose={onClose} title={period ? 'Edit Period' : 'New Period'} size="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">Name (e.g., "January 2024")</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'error' : ''}`}
            {...register('name')}
          />
          {errors.name && (
            <span className="form-error">{errors.name.message}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className={`form-input ${errors.startDate ? 'error' : ''}`}
              {...register('startDate')}
            />
            {errors.startDate && (
              <span className="form-error">{errors.startDate.message}</span>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className={`form-input ${errors.endDate ? 'error' : ''}`}
              {...register('endDate')}
            />
            {errors.endDate && (
              <span className="form-error">{errors.endDate.message}</span>
            )}
          </div>
        </div>

        {period && (
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" {...register('status')}>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
